import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { FC, Fragment, useState } from "react";
import { ButtonCloseContact } from "../molecules/ButtonCloseContact";
import { ConversationItem } from "../molecules/ConversationItem";
import { t } from "i18next";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { User } from "@/app/auth/auth";
import { ContactList } from "../molecules/ContactList";
import { ContactListItem } from "../molecules/ContactListItem";

export type ConversationsProps = {
  support?: boolean;
  isLoading?: boolean;
  conversations: Array<any>;
  user?: User;
  contacts: Array<User>;
  onConversationClick: (conversation: any) => void;
  visible: boolean;
  onClose: () => void;
  search?: string;
  onSearchChange: (search: string) => void;
  currentConversation?: any;
  onContactClick: (user: User) => void;
};

export const Conversations: FC<ConversationsProps> = ({
  isLoading,
  visible,
  user,
  search,
  contacts,
  onSearchChange,
  onClose,
  conversations,
  onConversationClick,
  onContactClick,
  currentConversation,
  support,
}) => {
  const [selectedTab, setSelectedTab] = useState<number>(1);

  return (
    <Fragment>
      <Dialog open={visible} onClose={onClose} className="relative z-10">
        <div className="fixed inset-0" />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
              >
                <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                        {user?.name}
                        <br />
                        <span className="text-sm text-gray-500">
                          {user?.role}
                        </span>
                      </DialogTitle>
                      <div className="ml-3 flex h-7 items-center">
                        <ButtonCloseContact onClick={onClose} />
                      </div>
                    </div>
                  </div>
                  <div className="border-b border-gray-200">
                    <div className="px-6">
                      <nav className="-mb-px flex space-x-6">
                        <a
                          href="#"
                          className={`${
                            selectedTab === 1
                              ? "border-indigo-500 text-primary-600"
                              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                          } whitespace-nowrap border-b-2 px-1 pb-4 text-sm font-medium`}
                          onClick={() => setSelectedTab(1)}
                        >
                          {t("chat.app.active")}
                        </a>
                        {!support && (
                          <Fragment>
                            <a
                              href="#"
                              className={`${
                                selectedTab === 2
                                  ? "border-indigo-500 text-primary-600"
                                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                              } whitespace-nowrap border-b-2 px-1 pb-4 text-sm font-medium`}
                              onClick={() => setSelectedTab(2)}
                            >
                              {t("chat.app.archived")}
                            </a>
                            <a
                              href="#"
                              className={`${
                                selectedTab === 3
                                  ? "border-indigo-500 text-primary-600"
                                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                              } whitespace-nowrap border-b-2 px-1 pb-4 text-sm font-medium`}
                              onClick={() => setSelectedTab(3)}
                            >
                              {t("chat.app.clients")}
                            </a>
                          </Fragment>
                        )}
                      </nav>
                    </div>
                  </div>
                  {selectedTab === 1 ? (
                    <>
                      <ul
                        role="list"
                        className="flex-1 divide-y divide-gray-200 overflow-y-auto"
                      >
                        {isLoading && (
                          <Fragment>
                            <ConversationItem isLoading />
                            <ConversationItem isLoading />
                            <ConversationItem isLoading />
                          </Fragment>
                        )}
                        {conversations
                          .filter((conversation) => conversation.status != 0)
                          .map((conversation) => (
                            <ConversationItem
                              key={conversation.id}
                              support={support}
                              conversation={conversation}
                              onClick={() => onConversationClick(conversation)}
                              current={
                                currentConversation?.conversationId ==
                                conversation.conversationId
                              }
                            />
                          ))}
                      </ul>
                    </>
                  ) : (
                    <>
                      {selectedTab === 2 ? (
                        <>
                          <ul
                            role="list"
                            className="flex-1 divide-y divide-gray-200 overflow-y-auto"
                          >
                            {isLoading && (
                              <Fragment>
                                <ConversationItem isLoading />
                                <ConversationItem isLoading />
                                <ConversationItem isLoading />
                              </Fragment>
                            )}
                            {conversations
                              .filter(
                                (conversation) => conversation.status == 0
                              )
                              .map((conversation) => (
                                <ConversationItem
                                  conversation={conversation}
                                  onClick={() =>
                                    onConversationClick(conversation)
                                  }
                                />
                              ))}
                          </ul>
                        </>
                      ) : (
                        <>
                          {selectedTab === 3 && (
                            <>
                              <div className="col-span-full px-5 mt-2">
                                <InputWithCornerHint
                                  label={t("chat.app.write_to_search")}
                                  value={search}
                                  onChange={(event) =>
                                    onSearchChange(event.target.value)
                                  }
                                />
                              </div>
                              <ul
                                role="list"
                                className="flex-1 divide-y divide-gray-200 overflow-y-auto"
                              >
                                <ContactList>
                                  {contacts.map((user) => (
                                    <ContactListItem
                                      key={user.id}
                                      user={user}
                                      onClick={() => onContactClick(user)}
                                    />
                                  ))}
                                </ContactList>
                              </ul>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </Fragment>
  );
};
